import { useContext, useEffect, useRef } from "react";
import { pushEvent, setPhase, getPhase } from "utilities";
import { ConfigurationContext } from "contexts";

/**
 *  useAnalytics hook.
 *
 *  Mirrors useLogger, deliberately as a parallel rather than an extension of it: logging lands in
 *  CloudWatch behind a Warning-level Serilog gate, analytics lands in GTM. Keeping them separate
 *  means neither constrains the other.
 *
 *  Every event carries session_id, which is what joins these events to the server-side funnel
 *  (srl_db.detection_sessions.short_id, and the Cloudflare Worker's exe_download log). Without it
 *  the page-side events are an island.
 */
export const useAnalytics = () => {
  const config = useContext(ConfigurationContext) || {};

  // Attached to every event. Mirrors useLogger's property block.
  const identity = {
    session_id: config.sessionId || "",
    computer_id: config.computerId || "",
    referrer_id: config.referrerId || 0,
    requirement_set_id: config.requirementSetId || 0,
  };

  const emit = (event, params) => pushEvent(event, { ...identity, ...(params || {}) });

  const started = useRef(Date.now());
  const elapsed = () => Math.round((Date.now() - started.current) / 1000);

  return {
    /** Visitor arrived and configuration resolved, so session_id now exists. */
    pageView: (params) => {
      started.current = Date.now();
      setPhase("arrived");
      emit("detection_page_view", params);
    },

    /** The library has been asked to fetch the binary. */
    downloadStarted: () => {
      setPhase("downloading");
      emit("detection_download_start", { elapsed_seconds: elapsed() });
    },

    /** The EXE has phoned home - progress id 30. Derived, needs no EXE change. */
    launched: () => {
      setPhase("running");
      emit("detection_exe_launched", { elapsed_seconds: elapsed() });
    },

    /**
     *  A library timeout fired and the visitor is now looking at the Detection Error screen.
     *
     *  This is the event the server-side funnel cannot produce: a timeout leaves the session record
     *  showing that nothing happened, indistinguishable from a visitor who never launched the EXE.
     *  `timeout_phase` is what separates "the download could not finish" from "the app never ran".
     *
     *  @param {string} timeoutPhase - load | run | network | extension
     */
    timedOut: (timeoutPhase) => {
      setPhase("timed_out");
      emit("detection_page_timeout", {
        timeout_phase: timeoutPhase,
        elapsed_seconds: elapsed(),
      });
    },

    /**
     *  The app timed out, with the phase derived rather than passed in.
     *
     *  The library raises one TimeoutError for both "the 5.6 MB download never finished"
     *  and "it downloaded and never connected back", which are opposite problems - the
     *  first is infrastructure, the second is the local handshake. It does not say which.
     *
     *  It does not have to: progress id 30 is the exe phoning home, so having reached the
     *  running phase means the binary arrived. No exe change and no library change, the
     *  same derivation `launched` already relies on.
     *
     *  Until this existed every app timeout was reported as `run`, so `load` had no call
     *  site at all and its count sat at zero - which reads as "downloads never stall"
     *  rather than "nobody ever asked".
     */
    appTimedOut: () => {
      const phase = getPhase() === "running" ? "run" : "load";
      setPhase("timed_out");
      emit("detection_page_timeout", {
        timeout_phase: phase,
        elapsed_seconds: elapsed(),
      });
    },

    /**
     *  A categorised failure. The session record carries no error code and its message field is
     *  empty in every row observed, so these ids are the only source of failure categories.
     *
     *  @param {string} category - AppError | AppCanceled | AppTimeout | ExtensionTimeout | ...
     */
    failed: (category) => {
      setPhase("failed");
      emit("detection_page_failed", {
        error_category: category,
        elapsed_seconds: elapsed(),
      });
    },

    /** Visitor clicked Download and Run again after an error. from_phase says what they retried from. */
    retried: () => {
      emit("detection_page_retry", { from_phase: getPhase(), elapsed_seconds: elapsed() });
      setPhase("retrying");
    },

    /** Detection finished on the page. */
    completed: () => {
      setPhase("completed");
      emit("detection_page_complete", { elapsed_seconds: elapsed() });
    },
  };
};

/**
 *  Best-effort "the visitor is leaving" signal. Register once, high in the tree.
 *
 *  Two deliberate choices:
 *
 *  1. `pagehide` only, NOT `visibilitychange`. On this page, tabbing away is the NORMAL path -
 *     the visitor switches to the download bar and then to the EXE. Treating hidden as abandonment
 *     would fire on almost every successful detection and mean nothing.
 *
 *  2. This event is corroboration, not the measurement. A dataLayer push during teardown is not
 *     reliably transmitted, so abandonment should be DERIVED from absence: a session with a
 *     page_view and a timeout but no retry and no completion has abandoned, whether or not this
 *     event arrived. Treat the count here as a floor.
 */
export const useAbandonTracking = () => {
  const config = useContext(ConfigurationContext) || {};
  const sent = useRef(false);

  useEffect(() => {
    const onLeave = () => {
      if (sent.current) return;
      sent.current = true;
      pushEvent("detection_page_abandon", {
        session_id: config.sessionId || "",
        computer_id: config.computerId || "",
        referrer_id: config.referrerId || 0,
        left_at_phase: getPhase(),
      });
    };

    window.addEventListener("pagehide", onLeave);
    return () => window.removeEventListener("pagehide", onLeave);
  }, [config.sessionId, config.computerId, config.referrerId]);
};
